// src/hooks/useAuth.ts
//
// Hook for managing authentication state.
// Hydrates auth state from /web/auth/me on mount.

import { useEffect, useCallback, useRef } from "react";
import { useAppStore, AuthUser } from "@/stores/appStore";
import { api, ApiError } from "@/lib/api";

// Refresh session every 30 minutes to prevent token expiration during active use
const SESSION_REFRESH_INTERVAL_MS = 30 * 60 * 1000;

interface MeResponse {
  user: AuthUser;
}

/**
 * Hook that provides authentication state and actions.
 *
 * On mount, checks /web/auth/me to hydrate auth state from HttpOnly cookies.
 * Returns current auth state and logout function.
 */
export function useAuth() {
  const { auth, setAuth, setAuthLoading, setAuthInitialized, logout: storeLogout } = useAppStore();

  // Check auth status on mount
  useEffect(() => {
    let mounted = true;

    async function checkAuth() {
      // Skip if already initialized
      if (auth.isInitialized) return;

      try {
        setAuthLoading(true);
        const response = await api.get<MeResponse>("/web/auth/me");

        if (mounted && response.user) {
          setAuth(response.user);
        }
      } catch {
        // Not authenticated or error - that's fine
        if (mounted) {
          setAuthInitialized();
        }
      }
    }

    checkAuth();

    return () => {
      mounted = false;
    };
  }, [auth.isInitialized, setAuth, setAuthLoading, setAuthInitialized]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      await api.post("/web/auth/logout", {});
    } catch {
      // Ignore errors - we'll clear local state anyway
    }
    storeLogout();
  }, [storeLogout]);

  return {
    user: auth.user,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    isInitialized: auth.isInitialized,
    sessionExpired: auth.sessionExpired,
    logout,
  };
}

/**
 * Hook that returns only the auth check function.
 * Useful for triggering a re-check after login.
 */
export function useAuthRefresh() {
  const { setAuth, setAuthLoading, setAuthInitialized } = useAppStore();

  const refreshAuth = useCallback(async () => {
    try {
      setAuthLoading(true);
      const response = await api.get<MeResponse>("/web/auth/me");

      if (response.user) {
        setAuth(response.user);
        return response.user;
      }
    } catch {
      setAuthInitialized();
    }
    return null;
  }, [setAuth, setAuthLoading, setAuthInitialized]);

  return { refreshAuth };
}

/**
 * Hook that keeps the session alive by periodically refreshing tokens.
 *
 * This prevents users from being logged out while actively using the dashboard.
 * The /web/auth/me endpoint automatically refreshes tokens if the access token
 * is expired but the refresh token is still valid.
 *
 * If the refresh fails (both access and refresh tokens expired), this hook
 * clears auth state so the user sees the login form immediately rather than
 * an empty dashboard.
 *
 * Should be used in authenticated layouts (e.g., dashboard layout).
 */
export function useSessionKeepAlive() {
  const { auth, setAuth, expireSession } = useAppStore();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only run if authenticated
    if (!auth.isAuthenticated) {
      return;
    }

    // Silent refresh function - doesn't update loading state to avoid UI flicker
    async function silentRefresh() {
      try {
        const response = await api.get<MeResponse>("/web/auth/me");
        if (response.user) {
          setAuth(response.user);
        }
      } catch (error) {
        // If the server says 401, the session is dead — log out immediately
        // so the user sees the login form instead of an empty dashboard
        if (error instanceof ApiError && error.status === 401) {
          expireSession();
        }
        // For network errors or 5xx, keep the session alive —
        // the user may just have a temporary connectivity issue
      }
    }

    // Set up periodic refresh
    intervalRef.current = setInterval(silentRefresh, SESSION_REFRESH_INTERVAL_MS);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [auth.isAuthenticated, setAuth, expireSession]);
}
